"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Glossaries = void 0;
const core_1 = require("../core");
/**
 * Glossaries help to explain some specific terms or the ones often used in the project so that they can be properly and consistently translated.
 *
 * Use API to manage glossaries or specific terms.
 * Glossary export and import are asynchronous operations and shall be completed with sequence of API methods.
 */
class Glossaries extends core_1.CrowdinApi {
    listGlossaries(options, deprecatedLimit, deprecatedOffset) {
        if ((0, core_1.isOptionalNumber)(options, '0' in arguments)) {
            options = { groupId: options, limit: deprecatedLimit, offset: deprecatedOffset };
        }
        let url = `${this.url}/glossaries`;
        url = this.addQueryParam(url, 'groupId', options.groupId);
        return this.getList(url, options.limit, options.offset);
    }
    /**
     * @param request request body
     * @see https://support.crowdin.com/api/v2/#operation/api.glossaries.post
     */
    addGlossary(request) {
        const url = `${this.url}/glossaries`;
        return this.post(url, request, this.defaultConfig());
    }
    /**
     * @param glossaryId glossary identifier
     * @see https://support.crowdin.com/api/v2/#operation/api.glossaries.get
     */
    getGlossary(glossaryId) {
        const url = `${this.url}/glossaries/${glossaryId}`;
        return this.get(url, this.defaultConfig());
    }
    /**
     * @param glossaryId glossary identifier
     * @see https://support.crowdin.com/api/v2/#operation/api.glossaries.delete
     */
    deleteGlossary(glossaryId) {
        const url = `${this.url}/glossaries/${glossaryId}`;
        return this.delete(url, this.defaultConfig());
    }
    /**
     * @param glossaryId glossary identifier
     * @param request request body
     * @see https://support.crowdin.com/api/v2/#operation/api.glossaries.patch
     */
    editGlossary(glossaryId, request) {
        const url = `${this.url}/glossaries/${glossaryId}`;
        return this.patch(url, request, this.defaultConfig());
    }
    /**
     * @param glossaryId glossary identifier
     * @param request request body
     * @see https://support.crowdin.com/api/v2/#operation/api.glossaries.exports.post
     */
    exportGlossary(glossaryId, request) {
        const url = `${this.url}/glossaries/${glossaryId}/exports`;
        return this.post(url, request, this.defaultConfig());
    }
    /**
     * @param glossaryId glossary identifier
     * @param exportId export identifier
     * @see https://support.crowdin.com/api/v2/#operation/api.glossaries.exports.get
     */
    checkGlossaryExportStatus(glossaryId, exportId) {
        const url = `${this.url}/glossaries/${glossaryId}/exports/${exportId}`;
        return this.get(url, this.defaultConfig());
    }
    /**
     * @param glossaryId glossary identifier
     * @param exportId export identifier
     * @see https://support.crowdin.com/api/v2/#operation/api.glossaries.exports.download.download
     */
    downloadGlossary(glossaryId, exportId) {
        const url = `${this.url}/glossaries/${glossaryId}/exports/${exportId}/download`;
        return this.get(url, this.defaultConfig());
    }
    /**
     * @param glossaryId glossary identifier
     * @param request request body
     * @see https://support.crowdin.com/api/v2/#operation/api.glossaries.imports.post
     */
    importGlossaryFile(glossaryId, request) {
        const url = `${this.url}/glossaries/${glossaryId}/imports`;
        return this.post(url, request, this.defaultConfig());
    }
    /**
     * @param glossaryId glossary identifier
     * @param importId import identifier
     * @see https://support.crowdin.com/api/v2/#operation/api.glossaries.imports.get
     */
    checkGlossaryImportStatus(glossaryId, importId) {
        const url = `${this.url}/glossaries/${glossaryId}/imports/${importId}`;
        return this.get(url, this.defaultConfig());
    }
    listTerms(glossaryId, options, deprecatedLimit, deprecatedOffset, deprecatedLanguageId, deprecatedTranslationOfTermId) {
        let url = `${this.url}/glossaries/${glossaryId}/terms`;
        if ((0, core_1.isOptionalNumber)(options, '1' in arguments)) {
            options = {
                userId: options,
                limit: deprecatedLimit,
                offset: deprecatedOffset,
                languageId: deprecatedLanguageId,
                translationOfTermId: deprecatedTranslationOfTermId,
            };
        }
        url = this.addQueryParam(url, 'userId', options.userId);
        url = this.addQueryParam(url, 'languageId', options.languageId);
        url = this.addQueryParam(url, 'translationOfTermId', options.translationOfTermId);
        return this.getList(url, options.limit, options.offset);
    }
    /**
     * @param glossaryId glossary identifier
     * @param request request body
     * @see https://support.crowdin.com/api/v2/#operation/api.glossaries.terms.post
     */
    addTerm(glossaryId, request) {
        const url = `${this.url}/glossaries/${glossaryId}/terms`;
        return this.post(url, request, this.defaultConfig());
    }
    clearGlossary(glossaryId, options, deprecatedTranslationOfTermId) {
        if ((0, core_1.isOptionalNumber)(options, '1' in arguments)) {
            options = { languageId: options, translationOfTermId: deprecatedTranslationOfTermId };
        }
        let url = `${this.url}/glossaries/${glossaryId}/terms`;
        url = this.addQueryParam(url, 'languageId', options.languageId);
        url = this.addQueryParam(url, 'translationOfTermId', options.translationOfTermId);
        return this.delete(url, this.defaultConfig());
    }
    /**
     * @param glossaryId glossary identifier
     * @param termId term identifier
     * @see https://support.crowdin.com/api/v2/#operation/api.glossaries.terms.get
     */
    getTerm(glossaryId, termId) {
        const url = `${this.url}/glossaries/${glossaryId}/terms/${termId}`;
        return this.get(url, this.defaultConfig());
    }
    /**
     * @param glossaryId glossary identifier
     * @param termId term identifier
     * @see https://support.crowdin.com/api/v2/#operation/api.glossaries.terms.delete
     */
    deleteTerm(glossaryId, termId) {
        const url = `${this.url}/glossaries/${glossaryId}/terms/${termId}`;
        return this.delete(url, this.defaultConfig());
    }
    /**
     * @param glossaryId glossary identifier
     * @param termId term identifier
     * @param request request body
     * @see https://support.crowdin.com/api/v2/#operation/api.glossaries.terms.patch
     */
    editTerm(glossaryId, termId, request) {
        const url = `${this.url}/glossaries/${glossaryId}/terms/${termId}`;
        return this.patch(url, request, this.defaultConfig());
    }
}
exports.Glossaries = Glossaries;
